import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { setActiveWorkspaceId } from './workspace-scope';
import type { DataEnvelope, Me, UpdateMeInput, User } from './types';

export const meKey = ['auth', 'me'] as const;

export function useMe() {
  return useQuery({
    queryKey: meKey,
    queryFn: ({ signal }) => api<DataEnvelope<Me>>('/auth/me', { signal }).then((r) => r.data),
    staleTime: 5 * 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) => {
      // A fresh session starts outside any client workspace (super admins pick one afterwards).
      setActiveWorkspaceId(null);
      return api<DataEnvelope<{ user: Me }>>('/auth/login', { method: 'POST', body: input }).then(
        (r) => r.data.user,
      );
    },
    onSuccess: (user) => qc.setQueryData(meKey, user),
  });
}

/** Self-serve sign-up: creates the workspace on a free trial and signs the owner in. */
export function useSignup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { companyName: string; name: string; email: string; password: string; website?: string }) => {
      setActiveWorkspaceId(null);
      return api<DataEnvelope<{ user: Me }>>('/auth/signup', { method: 'POST', body: input }).then(
        (r) => r.data.user,
      );
    },
    onSuccess: (user) => {
      qc.clear();
      qc.setQueryData(meKey, user);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api<void>('/auth/logout', { method: 'POST', body: {} }),
    onSettled: () => {
      setActiveWorkspaceId(null);
      qc.clear();
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateMeInput) =>
      api<DataEnvelope<User>>('/users/me', { method: 'PATCH', body: input }).then((r) => r.data),
    // Optimistic: status changes should feel instant.
    onMutate: async (input) => {
      await qc.cancelQueries({ queryKey: meKey });
      const prev = qc.getQueryData<Me>(meKey);
      if (prev) qc.setQueryData<Me>(meKey, { ...prev, ...input } as Me);
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(meKey, ctx.prev);
    },
    onSuccess: (user) => {
      qc.setQueryData<Me>(meKey, (prev) => (prev ? { ...prev, ...user } : prev));
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: { currentPassword: string; newPassword: string }) =>
      api<void>('/auth/change-password', { method: 'POST', body: input }),
  });
}
